import { html, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { classMap } from 'lit/directives/class-map.js';
import Player from '@vimeo/player';

@customElement('video-player')
export class VideoPlayer extends LitElement {

    // Props

	@property({ type: String })
    title: string = '';

    @property({ type: Number })
    position: number = 0;

    @property({ type: Boolean })
    paused: boolean = true;

    @property({ type: Boolean })
    muted: boolean = false;
    
    @query('iframe')
    _iframe!: HTMLElement;

    @query('[aria-label="seek position"]')
    _range!: HTMLInputElement;

    @state()
    private _interfaceIsVisible: boolean = true;

    @state()
    private _duration: number = 0;

    @state()
    private _scrubbing: boolean = false;

    @state()
    private _fullscreen: boolean = false;

    @state()
    private _html: string = '';

    private _player: Player | null = null;

    private _interfaceTimeout: number | null = null;

    // Lifecycle Hooks

    connectedCallback() {
        super.connectedCallback();
        this.classList.add('VideoPlayer');
    }

    firstUpdated() {
        const slot: HTMLElement | null = this.querySelector('[slot="embed"]');
        if (slot) {
            this._html = slot.innerHTML;
            slot.remove();
        }
    }

    async updated(props: Map<string,any>) {
        if (props.has('_html') && !this._player) {
            await this._initializePlayer();
        }
    }

    // Render

    render() {
        const classes = {
            'is-visible': this._interfaceIsVisible,
        };
        return html`
            <div 
                class="VideoPlayer-interface ${ classMap(classes) }" 
                @mousemove="${ this._showInterface }">
                <div class="VideoPlayer-controls">
                    ${ this.paused 
                        ?   html`<button 
                                class="VideoPlayer-button" 
                                aria-label="play" 
                                @click="${ this._play }">
                                <svg aria-hidden="true">
                                    <use href="#icon--play"/>
                                </svg>
                            </button>`
                        :   html`<button 
                                class="VideoPlayer-button" 
                                aria-label="pause" 
                                @click="${ this._pause }">
                                <svg aria-hidden="true">
                                    <use href="#icon--pause"/>
                                </svg>
                            </button>`
                    }
                    <div class="VideoPlayer-progress">
                        <div class="VideoPlayer-progressContainer">
                            <progress 
                                class="VideoPlayer-progressBar"
                                aria-label="playback progress"
                                min="0" max="1" step="0.001"
                                .value="${ this.position }">
                            </progress>
                            <input 
                                class="VideoPlayer-progressInput"
                                type="range" 
                                aria-label="seek slider" 
                                min="0" max="1" step="0.001"
                                .value="${ this.position }"
                                @mousedown="${ () => { this._scrubbing = true } }"
                                @mouseup="${ () => { this._scrubbing = false } }"
                                @change="${ this._setCurrentTime }"
                            />
                        </div>
                    </div>
                    ${ this.muted 
                        ?   html`<button 
                                class="VideoPlayer-button" 
                                aria-label="unmute" 
                                @click="${ this._unmute }">
                                <svg aria-hidden="true">
                                    <use href="#icon--unmute"/>
                                </svg>
                            </button>`
                        :   html`<button 
                                class="VideoPlayer-button" 
                                aria-label="mute" 
                                @click="${ this._mute }">
                                <svg aria-hidden="true">
                                    <use href="#icon--mute"/>
                                </svg>
                            </button>`
                    }
                    ${ this._fullscreen 
                        ?   html`<button 
                                class="VideoPlayer-button" 
                                aria-label="fullscreen-exit" 
                                @click="${ this._toggleFullscreen }">
                                <svg aria-hidden="true">
                                    <use href="#icon--fullscreen-exit"/>
                                </svg>
                            </button>`
                        :   html`<button 
                                class="VideoPlayer-button" 
                                aria-label="fullscreen" 
                                @click="${ this._toggleFullscreen }">
                                <svg aria-hidden="true">
                                    <use href="#icon--fullscreen"/>
                                </svg>
                            </button>`
                    }
                </div>
            </div>
            ${ unsafeHTML(this._html) }
        `;
    }

    createRenderRoot() { 
        return this;
    }

    // Methods

    private async _play() {
        try {
            await this._player?.play();
            this.paused = false;
            this._hideInterface();
        } catch (err) {
            console.error(err);
        }
    }

    private async _pause() {
        try {
            await this._player?.pause();
            this.paused = true;
            this._showInterface();
        } catch (err) {
            console.error(err);
        }
    }

    private async _mute() {
        try {
            await this._player?.setVolume(0);
            this.muted = true;
        } catch (err) {
            console.error(err);
        }
    }

    private async _unmute() {
        try {
            await this._player?.setVolume(1);
            this.muted = false;
        } catch (err) {
            console.error(err);
        }
    }

    private _toggleFullscreen() {
        if (document.fullscreenElement) {
            document.exitFullscreen();
            this._fullscreen = false;
        } else {
            this.requestFullscreen();
            this._fullscreen = true;
        }
    }

    private async _setCurrentTime() {
        const seconds: number = Math.floor(this._duration * Number(this._range.value));
        this.position = Number(this._range.value);
        try {
            await this._player?.setCurrentTime(seconds);
        } catch (err) {
            console.error(err);
        }
    }

    private _hideInterface() {
        this._interfaceTimeout = window.setTimeout(() => {
            this._interfaceIsVisible = false;
        }, 2000);
    }

    private _showInterface() {
        if (this._interfaceTimeout) {
            window.clearTimeout(this._interfaceTimeout);
        }
        this._interfaceIsVisible = true;
        if (!this.paused) {
            this._interfaceTimeout = window.setTimeout(() => {
                this._interfaceIsVisible = false;
            }, 2000);
        }
    }

    private async _initializePlayer() {
        if (this._iframe) {
            this._player = new Player(this._iframe);
            try {
                this._duration = await this._player.getDuration();
                this._player.on('timeupdate', (e) => {
                    if (!this._scrubbing) {
                        this.position = e.percent;
                    }
                });
            } catch (err) {
                console.error(err);
            }
        }
    }    

}